import GeoJSON from 'ol/format/GeoJSON'
import { Vector as VectorSource } from 'ol/source'
import { Vector as VectorLayer } from 'ol/layer'
import { Style, Circle, Fill, Stroke, Text, Icon, RegularShape } from 'ol/style'
import Feature from 'ol/Feature'
import { Select } from 'ol/interaction'
import Point from 'ol/geom/Point'
import LineString from 'ol/geom/LineString'
import moment from 'moment'
import zoomConf from '@/components/Map/MapZoomConfiguration';
import store from '@/store'
import OpenLayersLogic from './OpenLayersLogic'
import realtimeLogic from '@/services/realtimeLogic';

let vehicleFeatureStyles = [];              // [minZoomLevel]
let vehicleFeatureDeviationStyles = [];     // [deviationClass][minZoomLevel]
let mapView = null;

function getZoomConfig() {
    return zoomConf.getZoomConfig(mapView.getZoom());
}

/********************************************
 *  Tyylien määritykset                     *
 *******************************************/

function createSelectionIndicatorStyle() {
    const styleDot1 = new Style()
    const stroke = new Stroke({ color: 'rgba(80, 188, 217, 0.9)', width: 5 })
    const circle = new Circle({
        radius: 18,
        stroke: stroke
    })
    styleDot1.setImage(circle)

    /* Tehdään pieni temppu. lisätään valintatyyliin oma attribuutti
     * jolla tunnistetaan tämä tyyli valinnan osoittavaksi tyylisäännöksi */
    styleDot1.__selectiontype = true
    return styleDot1
}

function routeLineStyleFunction() {
    const lineColor = '#A6335D'

    const styles = [
        // linestring
        new Style({
            stroke: new Stroke({
                color: lineColor,
                width: 4,
            }),
        }),
    ]
    return styles
}

function scheduledRouteOutlineStyleFunction() {
    const lineColor = '#A6335D'
    const style = new Style({
        stroke: new Stroke({
            color: lineColor,
            width: 10,
        }),
    })
    return style
}

function gpsRouteLineStyleFunction() {
    const gpsLayerStyle = [
        new Style({
            stroke: new Stroke({
                color: '#0aac259d',
                width: 4,
            }),
        }),
    ]
    return gpsLayerStyle
}

function createBasePysakkiIcon(variant) {
    let circle_fill = '#4C82ff'
    let circle_stroke = '#20376C'

    if (variant == 'ON_DEMAND') {
        circle_fill = '#ad60d3'
        circle_stroke = '#5e3473'
    }
    if (variant == 'TRAM') {
        circle_fill = '#C8C800'
        circle_stroke = '#878700'
    }

    const circle = new Circle({
        fill: new Fill({ color: circle_fill + 'AA' }),
        stroke: new Stroke({ stroke: circle_stroke, width: 1 }),
        radius: 5,
    })
    const style = new Style()
    style.setImage(circle)
    return style
}

function createRoutePysakkiIcon() {

    let circle_fill = '#A6335D'
    const circle = new Circle({
        fill: new Fill({ color: circle_fill + 'AA' }),
        radius: 12,
    })

    const style = new Style()
    style.setImage(circle)
    return style

}


function createPysakkiClusterIcon() {
    const icon1 = new Icon({
        scale: 0.6,
        displacement: [0, 0],
        anchor: [0.5, 0.7],
        anchorYUnits: 'fraction',
        anchorXUnits: 'fraction',
        src: '/traveller-ui/map/images/icons/pysakki_group_grey_plain.svg',
    })
    const style = new Style()
    style.setImage(icon1)
    return style
}

function createBaseNayttoIcon() {
    const style = new Style({
        image: new Icon({
            src: '/traveller-ui/map/images/icons/dpi.png',
            scale: 0.2,
        }),
    })
    return style
}

function createVehicleLabel() {
    return new Text({
        text: '123',
        font: 'bold 0.8em Montserrat',
        offsetX: 0,
        offsetY: -3,
        textAlign: 'center',
        textBaseline: 'middle',
    })
}


function createBaseBusIcon() {
    const icon1 = new Icon({
        src: '/traveller-ui/map/images/icons/bus.svg',
        scale: 0.42,
    })
    const style = new Style()
    style.setImage(icon1)
    return style
}



function createBaseTrolleyBusIcon() {
    const icon1 = new Icon({
        src: '/traveller-ui/map/images/icons/trolleybus.svg',
        scale: 0.42,
    })
    const style = new Style()
    style.setImage(icon1)
    return style
}

function createBaseTramIcon() {
    const icon1 = new Icon({
        scale: 0.3,
        src: '/traveller-ui/map/images/icons/ratikka_02_plain.svg',
    })
    const style = new Style()
    style.setImage(icon1)
    return style
}

//FIXME: Is this function used anywhere?
function createVehicleFeatureStyle(feat) {
    const style = new Style()

    style.setText(createVehicleLabel(feat))
    const styleArr = []
    styleArr.push(style)
    const styleDot1 = new Style()

    const stroke = new Stroke({ color: '#ab3539', width: 2 })
    const fill = new Fill({ color: '#ff4f52' })
    const circle = new Circle({
        radius: 6,
        displacement: [-10, -10],
        stroke: stroke,
        fill: fill,
    })
    styleDot1.setImage(circle)
    styleArr.push(styleDot1)
    return styleArr
}

/* Luo tyylit ja asettaa ne datarakenteeseen. Tästä rakenteesta tehdään sitten viittaukset
 * kullekin featurelle jolloin tyyliolioita ei tehdä valtavasti vaan pelataan viittauksilla. */
function initStyles(deviationClasses, mapConfig, mView, routeStops) {
    mapView = mView;
    if (Array.isArray(mapConfig.zoomConfigs))
        zoomConf.setZoomConfigurations(mapConfig.zoomConfigs);

    const styles = {
        vehicles: {},
        stops: {},
        devices: {},
        lines: {},
        routes: {},
        route: {},
        selection: {},
        deviationClasses: deviationClasses,
        routeStops: { routeStops },
    }

    styles.stops.base = createBasePysakkiIcon()
    styles.stops.tram = createBasePysakkiIcon('TRAM')
    styles.stops.on_demand = createBasePysakkiIcon('ON_DEMAND')
    styles.stops.route = createRoutePysakkiIcon("ROUTESTOP")
    styles.stops.cluster = createPysakkiClusterIcon()
    styles.vehicles.base = {}
    styles.vehicles.base.bus = createBaseBusIcon()
    styles.vehicles.base.trolleybus = createBaseTrolleyBusIcon()
    styles.vehicles.base.tram = createBaseTramIcon()
    styles.vehicles.deviations = createVehicleScheduleDeviationStyles(deviationClasses)
    styles.devices.infopoint = createBaseNayttoIcon()
    styles.selection = createSelectionIndicatorStyle()
    styles.routes.line = routeLineStyleFunction // general
    styles.routes.scheduledRouteOutline = scheduledRouteOutlineStyleFunction
    styles.routes.gpsRoute = gpsRouteLineStyleFunction

    return styles
}

function createVehicleScheduleDeviationStyles(deviationClasses) {
    
    const RADIUS = getZoomConfig().vehicleCircleRadius;
    const STROKE_WIDTH = 4 

    const dot0 = new Style({
        image: new Circle({
            stroke: new Stroke({
                color: deviationClasses[0].primarycolor,
                width: STROKE_WIDTH,
            }),
            radius: RADIUS,
        }),
    })

    const dot1 = new Style({
        image: new Circle({
            stroke: new Stroke({
                color: deviationClasses[1].primarycolor,
                width: STROKE_WIDTH,
            }),
            radius: RADIUS,
        }),
    })

    const dot2 = new Style({
        image: new Circle({
            stroke: new Stroke({
                color: deviationClasses[2].primarycolor,
                width: STROKE_WIDTH,
            }),
            radius: RADIUS,
        }),
    })

    const dot3 = new Style({
        image: new Circle({
            stroke: new Stroke({
                color: deviationClasses[3].primarycolor,
                width: STROKE_WIDTH,
            }),
            radius: RADIUS,
        }),
    })

    const dot4 = new Style({
        image: new Circle({
            stroke: new Stroke({
                color: deviationClasses[4].primarycolor,
                width: STROKE_WIDTH,
            }),
            radius: RADIUS,
        }),
    })

    return [dot0, dot1, dot2, dot3, dot4]
}


function createTransparentCircle(STROKE_COLOR, STROKE_WIDTH, RADIUS) {
    return new Style({
        image: new Circle({
            stroke: new Stroke({ color: STROKE_COLOR, width: STROKE_WIDTH }),
            radius: RADIUS,
        }),
    })
}
function createDirectionalIndicator(feat, styleStructs) {
    if (!feat) {
        return null;
    }
    let RADIUS = getZoomConfig().vehicleCircleRadius;
    const props = feat.getProperties();
    var heading = props.direction;

    var triangleRadius = RADIUS;
    var bottomInset = -15;

    switch (RADIUS) {
        case 10:
            triangleRadius = 1.3;
            bottomInset = -15;
            break;
        case 15:
            triangleRadius = 1.35;
            bottomInset = -20;
            break;
        case 20:
            triangleRadius = 1.25;
            bottomInset = -25;
            break;
        case 25:
            triangleRadius = 0.7;
            bottomInset = -25;
            break;
        default:
            triangleRadius = 1.3;
            bottomInset = -15;
            break;
    }
    if(!heading) {
        triangleRadius = 0;
      }
    let deviationColor = NO_DEVIATION_COLOR

    if (props.deviation_class) 
    deviationColor = styleStructs.deviationClasses[props.deviation_class].primarycolor
    var directionInRadians = heading * (Math.PI / 180); 
  
    const triangle = new Style({
        image: new RegularShape({
            fill: new Fill({ color: deviationColor }),
            points: 3,
            radius: RADIUS * triangleRadius,
            angle: 0,
            rotation: directionInRadians,
            displacement: [0, -RADIUS * Math.cos(Math.PI / 6) - bottomInset], 
        }),
    });
  return triangle;
}

const NO_DEVIATION_COLOR = '#808080'

function createVehicleInfoLabel(feat, styleStructs) {
    const props = feat.getProperties()
    let vehicleInfo = props.vehicleLabel
    const line = getLine(props)
    if (line != null) vehicleInfo += '  ' + line
    
    if (props.deviation) {
        const deviation = realtimeLogic.getDeviationInRightFormat(
            props.lastSeenTime,
            props.deviation,
            props.blockSequence,
            props.realtimeStatus
        )
        if (deviation) vehicleInfo += '  ' + deviation
    }
    let deviationColor = NO_DEVIATION_COLOR
    if (props.deviation_class)
        deviationColor =
            styleStructs.deviationClasses[props.deviation_class].primarycolor

    return new Style({
        image: new RegularShape({
            fill: new Fill({ color: deviationColor }),
            stroke: new Stroke({ color: deviationColor, width: 2 }),
            displacement: [33, 0],
            points: 2,
            radius: 14,
            radius2: 0,
            angle: Math.PI / 2,
        }),
        text: new Text({
            offsetY: 1,
            offsetX: 37,
            text: vehicleInfo,
            placement: 'point', 
            textAlign: 'left',
            fill: new Fill({ color: '#000' }),
            backgroundFill: new Fill({ color: '#fff' }),
            backgroundStroke: new Stroke({ color: deviationColor, width: 3 }),
            padding: [5, 5, 3, 7],
            font: 'bold 13px Montserrat, Arial, Verdana, Helvetica, sans-serif',
        }),  
    })
}

function getStyleForVehicleFeature(feat, styleStructs) {
    if (styleStructs == null) {
        return;
    }

    const styleList = new Array();
    const featProps = feat.getProperties();
    if (featProps.deviation_class) { 
        styleList.push(getDeviationStyle(featProps, styleStructs));
     } else {
        styleList.push(getDefaultStyle(styleStructs));
     }
     styleList.push(createDirectionalIndicator(feat, styleStructs))
     styleList.push(createVehicleStatusIndicator(feat, styleStructs))
     if (featProps.deviation_class) {
        styleList.push(getDeviationStyle(featProps, styleStructs));
     }
     
     
    if (getZoomConfig().vehicleIconVisible) {
        styleList.push(getBaseStyle(feat, styleStructs));
        
    }
    
    if (getZoomConfig().vehicleInfoVisible) {
        styleList.push(createVehicleInfoLabel(feat, styleStructs));
    }
    return styleList;
}

function getDeviationStyle(featProps, styleStructs) {
    let circle = null;
    let zoomStyles = vehicleFeatureDeviationStyles[featProps.deviation_class];
    if (!zoomStyles) {
        vehicleFeatureDeviationStyles[featProps.deviation_class] = [];
    } else {
        let style = zoomStyles[getZoomConfig().minZoom]; 
        if (style) {
            circle = style;
        }
    }
    if (!circle) {
        let dot = styleStructs.vehicles.deviations[featProps.deviation_class];
        circle = new Style({
            image: new Circle({
                fill: dot.getImage().getFill(),
                stroke: dot.getImage().getStroke(),
                radius: getZoomConfig().vehicleCircleRadius,
            }),
        });
        vehicleFeatureDeviationStyles[featProps.deviation_class][getZoomConfig().minZoom] = circle;
    }
    return circle;
}

function getDefaultStyle() {

    let circle = vehicleFeatureStyles[getZoomConfig().minZoom];
    if (!circle) {
        circle = createTransparentCircle(NO_DEVIATION_COLOR, 4, getZoomConfig().vehicleCircleRadius);
        vehicleFeatureStyles[getZoomConfig().minZoom] = circle;
    }
    return circle;
}


function getBaseStyle(feat, styleStructs) {
    const vehicleType = getVehicleType(feat);
    let base = styleStructs.vehicles.base.bus;
    let iconScale = getZoomConfig().baseBusIcon.scale;

    switch (vehicleType) {
        case 'Tramwaj':
            base = styleStructs.vehicles.base.tram;
            iconScale = getZoomConfig().baseTramIcon.scale;
            break;
        case 'Trolejbus':
            base = styleStructs.vehicles.base.trolleybus;
            iconScale = getZoomConfig().baseTramIcon.scale;
            break;
    }

    let icon = base.getImage();
    if (icon) {
        icon.setScale(iconScale);
        base.setImage(icon);
    }

    return base;
}

function getVehicleType(feat) {
    try {
        const vehicleType = feat.getProperties().vehicleFeatures?.vehicle_type;
        if (vehicleType != null) {
            return vehicleType;
        }
        const vehicleType2 = feat.getProperties().vehicle?.features?.VEHICLE_TYPE;
        if (vehicleType2 != null) {
            return vehicleType2;
        }
        return "bus";
    } catch (err) {
        return "bus";
    }
}

function getStyleForStopFeature(feat, resolution, styleStructs) {
    // tarkistetaan onko tämä klusteri vai yksittäinen feature
    if (Array.isArray(feat.get('features')) && feat.get('features').length > 1) {
        feat.setStyle(styleStructs.stops.cluster)
    } else {
        try {
            const props = feat.getProperties()
            const data = props.stopData
            if (data.stopFeatures.ON_DEMAND == 'true') {
                feat.setStyle(styleStructs.stops.on_demand)
            } else if (data.stopFeatures.LINE_TYPE == '0') {
                feat.setStyle(styleStructs.stops.tram)
            } else {
                feat.setStyle(styleStructs.stops.base)
            }
        } catch (err) {
            feat.setStyle(styleStructs.stops.base)
        }
    }
}

function getStyleForRouteStopFeature(feat, resolution, styleStructs) {

    if (Array.isArray(feat.get('features')) && feat.get('features').length > 1) {
        feat.setStyle(styleStructs.stops.cluster)
    } else {
        try {
            const props = feat.getProperties()
            const data = props.stopData
            if (data.stopFeatures.ON_DEMAND == 'true') {
                feat.setStyle(styleStructs.stops.route)
            } else if (data.stopFeatures.LINE_TYPE == '0') {
                feat.setStyle(styleStructs.stops.route)
            } else {
                feat.setStyle(styleStructs.stops.route)
            }
        } catch (err) {
            feat.setStyle(styleStructs.stops.route)
        }
    }
}


function getStyleForDeviceFeature(feat, styleStructs) {
    if (styleStructs == null) {
        return
    }

    const styleList = new Array()

    styleList.push(styleStructs.devices.infopoint)
    styleList.push(createDeviceStatusIndicator(feat))

    return styleList
}

function createDeviceStatusIndicator(feat) {
    let statusColor = '#fff'

    const status = feat.getProperties().status
    if (status == 'ONLINE') statusColor = 'rgb(40, 167, 69)'
    if (status == 'OFFLINE') statusColor = 'rgb(220, 53, 69)'
    if (status == 'UNKNOWN') statusColor = 'rgb(204, 204, 204)'

    return new Style({
        image: new RegularShape({
            fill: new Fill({ color: statusColor }),
            points: 4,
            radius: 6,
            angle: Math.PI / 4,
            displacement: [0, 1],
        }),
    })
} 


function getStatusColor(status) { 

    let UIConfiguration = store.getters.uiConfiguration;
    if(!UIConfiguration) {
        return 'rgb(255, 255, 255)';
    }
    if(UIConfiguration.data.vehicle.showRealtimeColor == false || UIConfiguration.data.vehicle.showGpsColor == false) {
        return 'rgb(255, 255, 255)';
    }
    if (!status) {
        return 'rgb(255, 255, 255)';
    }
    if (status === 'no_driving_cycles') return 'rgb(217, 217, 217)';
    if (status === 'no_gps') return 'rgb(166, 166, 166)';
    if (status === 'ok') return 'rgb(255, 255, 255)';
    if (status === 'inactive') return 'rgb(102, 101, 101)';
    return 'rgb(255, 255, 255)';
}

function createVehicleStatusIndicator(feat) {
    if (!feat) {
        return null;
    }

    const RADIUS = getZoomConfig().vehicleCircleRadius;
    const props = feat.getProperties();
    const status = props ? props.realtimeStatus : null;
    const statusColor = getStatusColor(status);

    let circle = new Style({
        image: new Circle({
            fill: new Fill({ color: statusColor }),
            points: 30,
            radius: RADIUS + 2,
            angle: Math.PI / 4,
            displacement: [0, 0],
        }),
    });

    return circle;
}
function toggleSelectionStyle(feature, styleStructs) {
    const oldStyles = feature.getStyle()
    if (!Array.isArray(oldStyles)) {
        // tässä ei ollut selection-tyyliä
        const newStyle = [oldStyles, styleStructs.selection]
        feature.setStyle(newStyle)
        return
    }

    const last = oldStyles[oldStyles.length - 1]
    if (last.__selectiontype != undefined) {
        //valintatyyli on siis tyylilistan viimeinen tyyli. poistetaan se.
        oldStyles.pop()
        feature.setStyle(oldStyles)
    } else {
        oldStyles.push(styleStructs.selection)
        feature.setStyle(oldStyles)
    }
}

function getScheduleDeviationClass(v, classRules) {
    return OpenLayersLogic.getScheduleDeviationClass(v, classRules)
}

/********************************************
 *  Apufunktioita kartalle                  *
 *******************************************/
function createHistoricVehicleFeature(vehicleData) {
    const coordinates = [vehicleData.lon, vehicleData.lat]
    const feature = new Feature({
        geometry: new Point(coordinates),
        name: 'vehicle',
    })
    feature.setProperties(vehicleData)
    return feature
}

function createVehicleLayer() {
    const vectorSource = new VectorSource({
        format: new GeoJSON()
    })

    const vectorLayer = new VectorLayer({
        source: vectorSource,
        minZoom: zoomConf.getVehicleLayerMinZoom(),
    })

    return vectorLayer
}

function createStopLayer() {
    const vectorSource = new VectorSource({
        format: new GeoJSON({
            featureProjection: 'EPSG:3857',
            dataProjection: 'EPSG:3857',
        }),
    })

    const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: null,
        maxResolution: 10.0,
    })

    return vectorLayer
}

function createRouteStopLayer() {
    const vectorSource = new VectorSource({
        format: new GeoJSON({
            featureProjection: 'EPSG:3857',
            dataProjection: 'EPSG:3857',
        }),
    })

    const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: null,
        maxResolution: 10.0,
    })

    return vectorLayer
}


function createDeviceLayer() {
    const vectorSource = new VectorSource({
        format: new GeoJSON(),
    })

    const vectorLayer = new VectorLayer({
        source: vectorSource,
        minZoom: zoomConf.getDeviceLayerMinZoom()
    })

    return vectorLayer
}

function createRouteLayer(styleFunction) {
    const vectorSource = new VectorSource({
        format: new GeoJSON(),
    })

    const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: styleFunction,
    })

    return vectorLayer
}

function createSelectInteraction(layers, callback) {
    const select = new Select({
        style: null,
        layers: layers,
        hitTolerance: 4,
        multi: true,
    })
    select.on('select', callback)
    return select
}

function timeLimitExceeded(lastSeenTime) {
    let exceeded = true
    if (lastSeenTime) {
        const lastSeen = new moment(lastSeenTime, 'YYYY-MM-DDTHH:mm:ss.SSSZ')
        const now = new moment()
        lastSeen.add(30, 'm')
        exceeded = lastSeen.isBefore(now)
    }

    return exceeded
}


function formatTimestamp(timestamp) {
    if (timestamp) {
        var formattedTimestamp = moment(timestamp).format('DD.MM.YYYY HH:mm:ss');
        return formattedTimestamp
    }

    return timestamp
}
function getLine(properties) {
    let line = null
    try {
        if (properties.blockSequence == null) return null
        if (properties.lineLabel != null && properties.lineLabel != '')
            line = properties.lineLabel
    } catch (err) {
        return null
    }

    return line
}


function getWaitingTimeInRightFormat(waitingTime) {
    try {
        if(!waitingTime) return ''
        const time = new moment.unix(0)
        time.add(Math.abs(waitingTime) / 1000, 's')
        return time.format('mm:ss')
    } catch (e) {
        return ''
    }
}

function historyTimestampFormat(timestamp) {
    const stamp = new moment(timestamp, 'YYYY-MM-DDTHH:mm:ss.SSSZ')
    return stamp.format('DD.MM    -    HH:mm:ss')
}

function createRouteFromHistory(data) {
    let coords = []
    for (let i = 0; i < data.length; i++) {
        coords.push([data[i].lon, data[i].lat])
    }
    const lString = new LineString(coords)
    const feature = new Feature({
        geometry: lString,
        name: 'historyRoute',
    })

    return feature
}

function createRouteFromTOSHistory(data) {
    let coords = data.features[0].geometry.coordinates
    const lString = new LineString(coords)
    const feature = new Feature({
        geometry: lString,
        name: 'historyTosRoute',
    })

    return feature
}

function createRouteStopFeature(coordinates, stopID) {

    const feature = new Feature({
        geometry: new Point(coordinates),
        name: stopID,
        pointerEvents: 'none',
    })

    return feature
}

export default {
    createHistoricVehicleFeature,
    createVehicleLayer,
    createRouteLayer,
    createStopLayer,
    createRouteStopLayer,
    createDeviceLayer,
    getScheduleDeviationClass,
    initStyles,
    getStyleForVehicleFeature,
    getStyleForStopFeature,
    getStyleForRouteStopFeature,
    getStyleForDeviceFeature,
    createVehicleFeatureStyle,
    toggleSelectionStyle,
    createSelectInteraction,
    getWaitingTimeInRightFormat,
    timeLimitExceeded,
    formatTimestamp,
    historyTimestampFormat,
    createRouteFromHistory,
    createRouteFromTOSHistory,
    createRouteStopFeature,
}
